import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  List,
  ListItem,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Badge,
  Divider,
  ListItemIcon,
  ListItemText,
  Popover,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ReplyIcon from "@mui/icons-material/Reply";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PushPinIcon from "@mui/icons-material/PushPin";
import ForwardIcon from "@mui/icons-material/Forward";
import DownloadIcon from "@mui/icons-material/Download";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Avatar } from "@mui/material";
import { useMatrix } from "./MatrixProvider";
import { MatrixMessage, matrixClient } from "../../services/matrixClient";

/**
 * MatrixChatView — Full-featured Matrix chat component
 *
 * Features:
 * - Message list with auto-scroll & real-time updates
 * - Send/receive messages
 * - Edit/delete own messages
 * - Typing indicators
 * - Read receipts
 * - Emoji reactions (quick + picker)
 * - Reply to messages
 * - Pin/unpin messages
 * - File/image attachments
 * - Forward messages
 * - Login dialog for direct Synapse mode
 * - Load more (pagination)
 */

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🔥"];

// ==================== LOGIN DIALOG ====================

const MatrixLoginDialog: React.FC<{
  open: boolean;
  onLogin: (user: string, password: string) => Promise<void>;
  error: string | null;
  isLoading: boolean;
}> = ({ open, onLogin, error, isLoading }) => {
  const [user, setUser] = useState("admin");
  const [password, setPassword] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onLogin(user, password);
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Matrix Login</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            fullWidth
            variant="outlined"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            disabled={isLoading}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : "Login"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ==================== EMOJI PICKER POPOVER ====================

const EmojiPickerPopover: React.FC<{
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelect: (emoji: string) => void;
}> = ({ anchorEl, open, onClose, onSelect }) => {
  const allEmojis = [
    "😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃",
    "😉", "😊", "😇", "🥰", "😍", "🤩", "😘", "😗", "😚", "😙",
    "😋", "😛", "😜", "🤪", "😝", "🤑", "🤗", "🤭", "🤫", "🤔",
    "🤐", "🤨", "😐", "😑", "😶", "😏", "😒", "🙄", "😬", "🤥",
    "😌", "😔", "😪", "🤤", "😴", "😷", "🤒", "🤕", "🤢", "🤮",
    "🤧", "🥵", "🥶", "🥴", "😵", "🤯", "🤠", "🥳", "😎", "🤓",
    "👍", "👎", "👏", "🙌", "🤝", "🙏", "💪", "❤️", "🔥", "⭐",
    "💯", "✅", "❌", "⚡", "🎉", "🎊", "💡", "📌", "🚀", "💎",
  ];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Box
        sx={{
          p: 1,
          display: "grid",
          gridTemplateColumns: "repeat(10, 1fr)",
          gap: "2px",
          maxWidth: 350,
          maxHeight: 300,
          overflow: "auto",
        }}
      >
        {allEmojis.map((emoji) => (
          <IconButton
            key={emoji}
            size="small"
            onClick={() => {
              onSelect(emoji);
              onClose();
            }}
            sx={{ fontSize: "1.3rem", padding: "4px" }}
          >
            {emoji}
          </IconButton>
        ))}
      </Box>
    </Popover>
  );
};

// ==================== REACTIONS DISPLAY ====================

const ReactionsDisplay: React.FC<{
  reactions: Record<string, Array<{ reactionEventId: string; sender: string }>>;
  currentUserId: string;
  onAddReaction: (emoji: string) => void;
  onRemoveReaction: (reactionEventId: string) => void;
}> = ({ reactions, currentUserId, onAddReaction, onRemoveReaction }) => {
  if (!reactions || Object.keys(reactions).length === 0) return null;

  const formatUser = (u: string) => {
    if (u.startsWith("@")) return u.split(":")[0].substring(1);
    return u;
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: 0.5 }}>
      {Object.entries(reactions).map(([emoji, reactionInfos]) => {
        const myReaction = reactionInfos.find((r) => r.sender === currentUserId);
        const tooltipText = reactionInfos.map((r) => formatUser(r.sender)).join(", ");

        return (
          <Tooltip key={emoji} title={tooltipText}>
            <Chip
              label={`${emoji} ${reactionInfos.length}`}
              size="small"
              variant={myReaction ? "filled" : "outlined"}
              color={myReaction ? "primary" : "default"}
              onClick={() => {
                if (myReaction) {
                  onRemoveReaction(myReaction.reactionEventId);
                } else {
                  onAddReaction(emoji);
                }
              }}
              sx={{
                height: 24,
                fontSize: "0.75rem",
                cursor: "pointer",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

// ==================== REPLY PREVIEW ====================

const ReplyPreview: React.FC<{
  replyToEventId: string;
  messages: MatrixMessage[];
}> = ({ replyToEventId, messages }) => {
  const parentMsg = messages.find((m) => m.eventId === replyToEventId);
  if (!parentMsg) return null;

  const formatSender = (sender: string) => {
    if (sender.startsWith("@")) return sender.split(":")[0].substring(1);
    return sender;
  };

  return (
    <Box
      sx={{
        borderLeft: "3px solid #1976d2",
        pl: 1,
        mb: 0.5,
        opacity: 0.8,
        maxHeight: 40,
        overflow: "hidden",
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, color: "#1976d2" }}>
        {formatSender(parentMsg.sender)}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#666" }} noWrap>
        {parentMsg.content}
      </Typography>
    </Box>
  );
};

// ==================== ATTACHMENT DISPLAY ====================

const AttachmentDisplay: React.FC<{
  attachments: MatrixMessage["attachments"];
  msgtype?: string;
}> = ({ attachments, msgtype }) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <Box sx={{ mt: 0.5 }}>
      {attachments.map((att, idx) => {
        const mediaUrl = matrixClient.getMediaUrl(att.url);

        // Use msgtype as primary indicator (more reliable than mimetype)
        const isAudio = msgtype === "m.audio" || att.mimetype?.startsWith("audio/");
        const isVideo = !isAudio && (msgtype === "m.video" || att.mimetype?.startsWith("video/"));
        const isImage = !isAudio && !isVideo && (msgtype === "m.image" || att.mimetype?.startsWith("image/"));

        if (isImage) {
          return (
            <Box key={idx} sx={{ mt: 0.5, mb: 0.5 }}>
              <img
                src={mediaUrl}
                alt={att.filename}
                style={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  borderRadius: 8,
                  cursor: "pointer",
                }}
                onClick={() => window.open(mediaUrl, "_blank")}
              />
            </Box>
          );
        }

        if (isVideo) {
          const isVideoCircle = att.filename === "Video message" || att.filename === "Video message back";
          if (isVideoCircle) {
            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  position: "relative",
                  width: 200,
                  height: 200,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  const video = e.currentTarget.querySelector("video");
                  if (video) {
                    if (video.paused) {
                      // Remove #t fragment for full playback
                      if (video.src.includes("#t=")) {
                        video.src = video.src.split("#")[0];
                      }
                      video.currentTime = 0;
                      video.muted = false;
                      video.play();
                      const overlay = e.currentTarget.querySelector("[data-play-overlay]") as HTMLElement;
                      if (overlay) overlay.style.display = "none";
                    } else {
                      video.pause();
                      const overlay = e.currentTarget.querySelector("[data-play-overlay]") as HTMLElement;
                      if (overlay) overlay.style.display = "flex";
                    }
                  }
                }}
              >
                <video
                  src={mediaUrl + "#t=0.1"}
                  playsInline
                  preload="auto"
                  style={{
                    width: 200,
                    height: 200,
                    borderRadius: "50%",
                    objectFit: "cover",
                    backgroundColor: "#000",
                  }}
                  onEnded={(e) => {
                    const v = e.currentTarget as HTMLVideoElement;
                    v.currentTime = 0;
                    const overlay = v.parentElement?.querySelector("[data-play-overlay]") as HTMLElement;
                    if (overlay) overlay.style.display = "flex";
                  }}
                />
                <Box
                  data-play-overlay=""
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <PlayArrowIcon sx={{ color: "#fff", fontSize: 32 }} />
                </Box>
              </Box>
            );
          }
          return (
            <Box key={idx} sx={{ mt: 0.5 }}>
              <video
                src={mediaUrl}
                controls
                style={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  borderRadius: 8,
                }}
              />
            </Box>
          );
        }

        if (isAudio) {
          return (
            <Box key={idx} sx={{ mt: 0.5 }}>
              <audio src={mediaUrl} controls style={{ width: "100%" }} />
            </Box>
          );
        }

        // Generic file
        return (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
              p: 1,
              borderRadius: 1,
              backgroundColor: "rgba(0,0,0,0.05)",
              cursor: "pointer",
            }}
            onClick={() => window.open(mediaUrl, "_blank")}
          >
            <InsertDriveFileIcon sx={{ fontSize: 20 }} />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {att.filename}
              </Typography>
              {att.size && (
                <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
                  {(att.size / 1024).toFixed(1)} KB
                </Typography>
              )}
            </Box>
            <DownloadIcon sx={{ fontSize: 16, ml: "auto", opacity: 0.6 }} />
          </Box>
        );
      })}
    </Box>
  );
};

// ==================== MESSAGE ITEM ====================

interface MessageItemProps {
  message: MatrixMessage;
  isOwnMessage: boolean;
  isPinned: boolean;
  onEdit: (eventId: string, currentContent: string) => void;
  onDelete: (eventId: string) => void;
  onReply: (eventId: string) => void;
  onReact: (eventId: string, emoji: string) => void;
  onRemoveReaction: (reactionEventId: string) => void;
  onPin: (eventId: string) => void;
  onUnpin: (eventId: string) => void;
  onForward: (content: string) => void;
  readBy: string[];
  reactions: Record<string, Array<{ reactionEventId: string; sender: string }>>;
  currentUserId: string;
  allMessages: MatrixMessage[];
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOwnMessage,
  isPinned,
  onEdit,
  onDelete,
  onReply,
  onReact,
  onRemoveReaction,
  onPin,
  onUnpin,
  onForward,
  readBy,
  reactions,
  currentUserId,
  allMessages,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<null | HTMLElement>(null);
  const [showQuickReactions, setShowQuickReactions] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatSender = (sender: string) => {
    if (sender.startsWith("@")) {
      const parts = sender.split(":");
      return parts[0].substring(1);
    }
    return sender;
  };

  const isFileMessage = message.attachments && message.attachments.length > 0;
  const isVideoCircle = isFileMessage && message.msgtype === "m.video" && message.attachments!.some(
    (a) => a.filename === "Video message" || a.filename === "Video message back"
  );

  return (
    <ListItem
      data-event-id={message.eventId}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isOwnMessage ? "flex-end" : "flex-start",
        padding: "4px 16px",
        transition: "background-color 0.3s ease",
      }}
      onMouseEnter={() => setShowQuickReactions(true)}
      onMouseLeave={() => setShowQuickReactions(false)}
    >
      {/* Pin indicator */}
      {isPinned && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mb: 0.5,
            opacity: 0.6,
          }}
        >
          <PushPinIcon sx={{ fontSize: 12 }} />
          <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
            Pinned message
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, maxWidth: "70%", minWidth: "120px" }}>
        {/* Sender avatar (for other people's messages) */}
        {!isOwnMessage && (
          <Avatar
            src={message.senderAvatar ? matrixClient.getMediaUrl(message.senderAvatar) : undefined}
            sx={{ width: 28, height: 28, mb: "2px", flexShrink: 0, fontSize: 12 }}
          >
            {(message.senderDisplayName || message.sender || "?")[0]?.toUpperCase()}
          </Avatar>
        )}
      <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
        {/* Quick reactions bar (shows on hover) */}
        {showQuickReactions && !message.redacted && (
          <Box
            sx={{
              position: "absolute",
              top: -28,
              [isOwnMessage ? "right" : "left"]: 0,
              display: "flex",
              gap: "1px",
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
              padding: "2px 4px",
              zIndex: 10,
            }}
          >
            {QUICK_REACTIONS.map((emoji) => (
              <IconButton
                key={emoji}
                size="small"
                onClick={() => onReact(message.eventId, emoji)}
                sx={{ fontSize: "0.9rem", padding: "2px", minWidth: 28 }}
              >
                {emoji}
              </IconButton>
            ))}
            <IconButton
              size="small"
              onClick={(e) => setEmojiAnchor(e.currentTarget)}
              sx={{ fontSize: "0.9rem", padding: "2px", minWidth: 28 }}
            >
              <EmojiEmotionsIcon sx={{ fontSize: 16, opacity: 0.5 }} />
            </IconButton>
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: isVideoCircle
              ? "transparent"
              : message.redacted
              ? "#ffebee"
              : isOwnMessage
              ? "#222222"
              : "#f5f5f5",
            color: message.redacted
              ? "#b71c1c"
              : isOwnMessage
              ? "#fff"
              : "#000",
            borderRadius: isVideoCircle ? 0 : "12px",
            padding: isVideoCircle ? 0 : "8px 12px",
            position: "relative",
            opacity: message.redacted ? 0.7 : 1,
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            if (!message.redacted) {
              setAnchorEl(e.currentTarget as HTMLElement);
            }
          }}
        >
          {!isOwnMessage && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                display: "block",
                color: isOwnMessage ? "rgba(255,255,255,0.8)" : "#333",
              }}
            >
              {formatSender(message.senderDisplayName || message.sender)}
            </Typography>
          )}

          {/* Reply preview */}
          {message.replyTo && (
            <ReplyPreview replyToEventId={message.replyTo} messages={allMessages} />
          )}

          {/* Text content */}
          {message.content && !isFileMessage && (
            <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
              {message.content}
            </Typography>
          )}

          {/* Attachments */}
          {isFileMessage && (
            <AttachmentDisplay attachments={message.attachments} msgtype={message.msgtype} />
          )}
          {isFileMessage && message.content && message.msgtype === "m.text" && (
            <Typography variant="body1" sx={{ wordBreak: "break-word", mt: 0.5 }}>
              {message.content}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "4px",
              marginTop: "2px",
            }}
          >
            {message.edited && (
              <Typography
                variant="caption"
                sx={{ opacity: 0.6, fontStyle: "italic", fontSize: "0.65rem" }}
              >
                edited
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ opacity: 0.7, fontSize: "0.7rem" }}
            >
              {formatTime(message.timestamp)}
            </Typography>
            {isOwnMessage && !message.redacted && (
              <Tooltip
                title={readBy.length > 0 ? `Read by: ${readBy.join(", ")}` : "Sent"}
              >
                {readBy.length > 0 ? (
                  <DoneAllIcon
                    sx={{
                      fontSize: 14,
                      opacity: 0.7,
                      color: isOwnMessage ? "#b3e5fc" : "#4caf50",
                    }}
                  />
                ) : (
                  <DoneIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                )}
              </Tooltip>
            )}
            {!message.redacted && (
              <IconButton
                size="small"
                sx={{
                  padding: "1px",
                  color: isOwnMessage
                    ? "rgba(255,255,255,0.6)"
                    : "rgba(0,0,0,0.4)",
                }}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <MoreVertIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Reactions display */}
        <ReactionsDisplay
          reactions={reactions}
          currentUserId={currentUserId}
          onAddReaction={(emoji) => onReact(message.eventId, emoji)}
          onRemoveReaction={onRemoveReaction}
        />
      </Box>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onReply(message.eventId);
          }}
        >
          <ListItemIcon>
            <ReplyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reply</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            setAnchorEl(null);
            setEmojiAnchor(e.currentTarget);
          }}
        >
          <ListItemIcon>
            <EmojiEmotionsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>React</ListItemText>
        </MenuItem>
        {isPinned ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onUnpin(message.eventId);
            }}
          >
            <ListItemIcon>
              <PushPinOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Unpin</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onPin(message.eventId);
            }}
          >
            <ListItemIcon>
              <PushPinIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Pin</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onForward(message.content);
          }}
        >
          <ListItemIcon>
            <ForwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Forward</ListItemText>
        </MenuItem>
        {isOwnMessage && (
          <Divider />
        )}
        {isOwnMessage && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onEdit(message.eventId, message.content);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {isOwnMessage && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onDelete(message.eventId);
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Emoji Picker Popover */}
      <EmojiPickerPopover
        anchorEl={emojiAnchor}
        open={Boolean(emojiAnchor)}
        onClose={() => setEmojiAnchor(null)}
        onSelect={(emoji) => onReact(message.eventId, emoji)}
      />
    </ListItem>
  );
};

// ==================== TYPING INDICATOR ====================

const TypingIndicator: React.FC<{ users: string[] }> = ({ users }) => {
  if (users.length === 0) return null;

  const formatUser = (u: string) => {
    if (u.startsWith("@")) return u.split(":")[0].substring(1);
    return u;
  };

  const text =
    users.length === 1
      ? `${formatUser(users[0])} is typing...`
      : users.length === 2
      ? `${formatUser(users[0])} and ${formatUser(users[1])} are typing...`
      : `${formatUser(users[0])} and ${users.length - 1} others are typing...`;

  return (
    <Box sx={{ px: 2, py: 0.5, minHeight: 20 }}>
      <Typography
        variant="caption"
        sx={{ color: "#999", fontStyle: "italic" }}
      >
        {text}
      </Typography>
    </Box>
  );
};

// ==================== ROOM SELECTOR ====================

const RoomSelector: React.FC<{
  rooms: Array<{ id: string; name: string }>;
  onSelect: (roomId: string) => void;
  isLoading: boolean;
  onRefresh: () => void;
}> = ({ rooms, onSelect, isLoading, onRefresh }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Matrix Rooms
        </Typography>
        <Button size="small" onClick={onRefresh} disabled={isLoading}>
          Refresh
        </Button>
      </Box>
      {isLoading && <CircularProgress size={24} />}
      {rooms.length === 0 && !isLoading && (
        <Typography color="textSecondary">
          No rooms found. Try refreshing.
        </Typography>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {rooms.map((room) => (
          <Chip
            key={room.id}
            label={room.name || room.id}
            onClick={() => onSelect(room.id)}
            clickable
            variant="outlined"
            color="primary"
          />
        ))}
      </Box>
    </Box>
  );
};

// ==================== FORWARD DIALOG ====================

const ForwardDialog: React.FC<{
  open: boolean;
  content: string;
  rooms: Array<{ id: string; name: string }>;
  onForward: (toRoomId: string, content: string) => void;
  onClose: () => void;
}> = ({ open, content, rooms, onForward, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Forward Message</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            p: 1,
            mb: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
            maxHeight: 100,
            overflow: "auto",
          }}
        >
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            {content}
          </Typography>
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Select a room to forward to:
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {rooms.map((room) => (
            <Button
              key={room.id}
              variant="outlined"
              size="small"
              onClick={() => {
                onForward(room.id, content);
                onClose();
              }}
              sx={{ justifyContent: "flex-start", textTransform: "none" }}
            >
              {room.name || room.id}
            </Button>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== MAIN CHAT VIEW ====================

interface MatrixChatViewProps {
  roomId?: string;
  currentUserId?: string;
}

const MatrixChatView: React.FC<MatrixChatViewProps> = ({
  roomId,
  currentUserId,
}) => {
  const {
    isLoggedIn,
    userId,
    messages,
    rooms,
    isLoading,
    error,
    typingUsers,
    readReceipts,
    reactions,
    pinnedEventIds,
    login,
    setCurrentRoom,
    sendMessage,
    editMessage,
    deleteMessage,
    loadMoreMessages,
    sendTyping,
    markRead,
    refreshRooms,
    currentRoom,
    sendReply,
    sendReaction,
    removeReaction,
    pinMessage,
    unpinMessage,
    uploadAndSendFile,
    forwardMessage,
  } = useMatrix();

  const [inputValue, setInputValue] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [forwardContent, setForwardContent] = useState<string | null>(null);
  const [attachMenuAnchor, setAttachMenuAnchor] = useState<null | HTMLElement>(null);
  const [inputEmojiAnchor, setInputEmojiAnchor] = useState<null | HTMLElement>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSendingFiles, setIsSendingFiles] = useState(false);
  const [pinAnchor, setPinAnchor] = useState<null | HTMLElement>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const effectiveUserId = currentUserId || userId;

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Set room when roomId prop changes
  useEffect(() => {
    if (roomId && isLoggedIn) {
      setCurrentRoom(roomId);
    }
  }, [roomId, isLoggedIn, setCurrentRoom]);

  // Mark last message as read
  useEffect(() => {
    if (messages.length > 0 && isLoggedIn) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.sender !== effectiveUserId) {
        markRead(lastMsg.eventId);
      }
    }
  }, [messages, markRead, effectiveUserId, isLoggedIn]);

  // ── Handlers ─────────────────────────────────

  const handleLogin = async (user: string, password: string) => {
    setLoginLoading(true);
    setLoginError(null);
    try {
      await login(user, password);
    } catch (err: any) {
      setLoginError(
        err?.response?.data?.error || err.message || "Login failed"
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSend = useCallback(() => {
    if (!inputValue.trim()) return;
    if (replyToId) {
      sendReply(replyToId, inputValue.trim()).catch(() => {});
      setReplyToId(null);
    } else {
      sendMessage(inputValue.trim()).catch(() => {});
    }
    setInputValue("");
    sendTyping(false);
  }, [inputValue, replyToId, sendMessage, sendReply, sendTyping]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (e.target.value.length > 0) {
      sendTyping(true);
    } else {
      sendTyping(false);
    }
  };

  const handleStartEdit = (eventId: string, currentContent: string) => {
    setEditingId(eventId);
    setEditValue(currentContent);
    setReplyToId(null);
  };

  const handleConfirmEdit = async () => {
    if (editingId && editValue.trim()) {
      try {
        await editMessage(editingId, editValue.trim());
      } catch {
        /* error shown via context */
      }
    }
    setEditingId(null);
    setEditValue("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId) {
      try {
        await deleteMessage(deleteConfirmId);
      } catch {
        /* error shown via context */
      }
    }
    setDeleteConfirmId(null);
  };

  const handleScroll = () => {
    if (listRef.current && listRef.current.scrollTop === 0) {
      loadMoreMessages();
    }
  };

  const handleSelectRoom = (selectedRoomId: string) => {
    setCurrentRoom(selectedRoomId);
  };

  const handleReply = (eventId: string) => {
    setReplyToId(eventId);
    setEditingId(null);
  };

  const handleReact = (eventId: string, emoji: string) => {
    sendReaction(eventId, emoji);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setPendingFiles((prev) => [...prev, ...Array.from(files)]);
    e.target.value = "";
  };

  const handleSendPendingFiles = async () => {
    if (pendingFiles.length === 0) return;
    setIsSendingFiles(true);
    for (const file of pendingFiles) {
      try {
        await uploadAndSendFile(file);
      } catch { /* error handled in provider */ }
    }
    setPendingFiles([]);
    setIsSendingFiles(false);
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleForwardStart = (content: string) => {
    setForwardContent(content);
  };

  const handleForward = (toRoomId: string, content: string) => {
    forwardMessage(toRoomId, content).catch(() => {});
  };

  const handleEmojiForInput = (emoji: string) => {
    setInputValue((prev) => prev + emoji);
  };

  // Compute read-by per message
  const getReadBy = (eventId: string): string[] => {
    const readers: string[] = [];
    for (const [uid, lastReadEvt] of Object.entries(readReceipts)) {
      if (uid !== effectiveUserId && lastReadEvt === eventId) {
        readers.push(
          uid.startsWith("@") ? uid.split(":")[0].substring(1) : uid
        );
      }
    }
    return readers;
  };

  // Get reply-to message for the reply bar
  const replyToMessage = replyToId
    ? messages.find((m) => m.eventId === replyToId)
    : null;

  const formatSender = (sender: string) => {
    if (sender?.startsWith("@")) return sender.split(":")[0].substring(1);
    return sender || "";
  };

  // ── Render ───────────────────────────────────

  if (!isLoggedIn) {
    return (
      <MatrixLoginDialog
        open={true}
        onLogin={handleLogin}
        error={loginError}
        isLoading={loginLoading}
      />
    );
  }

  if (!roomId && !currentRoom) {
    return (
      <Paper sx={{ minHeight: 400 }}>
        <RoomSelector
          rooms={rooms}
          onSelect={handleSelectRoom}
          isLoading={isLoading}
          onRefresh={refreshRooms}
        />
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "600px",
        maxHeight: "calc(100vh - 200px)",
      }}
    >
      {/* Room Header */}
      {currentRoom && (
        <Box
          sx={{
            p: 1.5,
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {currentRoom.name}
            </Typography>
            {currentRoom.memberCount && (
              <Typography variant="caption" sx={{ color: "#999" }}>
                ({currentRoom.memberCount} members)
              </Typography>
            )}
            {pinnedEventIds.length > 0 && (
              <>
                <Tooltip title={`${pinnedEventIds.length} pinned message(s) — click to view`}>
                  <Badge
                    badgeContent={pinnedEventIds.length}
                    color="primary"
                    sx={{ ml: 1, cursor: "pointer" }}
                    onClick={(e) => setPinAnchor(e.currentTarget)}
                  >
                    <PushPinIcon sx={{ fontSize: 18, color: "#999" }} />
                  </Badge>
                </Tooltip>
                <Popover
                  open={Boolean(pinAnchor)}
                  anchorEl={pinAnchor}
                  onClose={() => setPinAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                  <Box sx={{ p: 1, minWidth: 280, maxWidth: 400, maxHeight: 300, overflow: "auto" }}>
                    <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, fontWeight: 600 }}>
                      Pinned Messages
                    </Typography>
                    <Divider sx={{ my: 0.5 }} />
                    {pinnedEventIds.map((eventId: string) => {
                      const msg = messages.find((m) => m.eventId === eventId);
                      return (
                        <Box
                          key={eventId}
                          sx={{
                            px: 1, py: 0.75, cursor: "pointer", borderRadius: 1,
                            "&:hover": { backgroundColor: "#f0f0f0" },
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                          }}
                          onClick={() => {
                            setPinAnchor(null);
                            const el = document.querySelector(`[data-event-id="${eventId}"]`);
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth", block: "center" });
                              (el as HTMLElement).style.backgroundColor = "#fff9c4";
                              setTimeout(() => { (el as HTMLElement).style.backgroundColor = ""; }, 2000);
                            }
                          }}
                        >
                          <Box sx={{ overflow: "hidden", mr: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {msg ? formatSender(msg.sender) : "Unknown"}
                            </Typography>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                              {msg?.content || eventId}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); unpinMessage(eventId); }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                </Popover>
              </>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: "#4caf50" }}>
            ● Connected
          </Typography>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ m: 1 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {isLoading && messages.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexGrow: 1,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Message List */}
          <List
            ref={listRef}
            sx={{ flexGrow: 1, overflow: "auto", padding: 0 }}
            onScroll={handleScroll}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  color: "#999",
                }}
              >
                <Typography variant="body1">
                  No messages yet. Start the conversation!
                </Typography>
              </Box>
            ) : (
              messages.map((message) => (
                <MessageItem
                  key={message.eventId}
                  message={message}
                  isOwnMessage={
                    message.sender === effectiveUserId ||
                    message.sender === "self"
                  }
                  isPinned={pinnedEventIds.includes(message.eventId)}
                  onEdit={handleStartEdit}
                  onDelete={(id) => setDeleteConfirmId(id)}
                  onReply={handleReply}
                  onReact={handleReact}
                  onRemoveReaction={removeReaction}
                  onPin={pinMessage}
                  onUnpin={unpinMessage}
                  onForward={handleForwardStart}
                  readBy={getReadBy(message.eventId)}
                  reactions={reactions[message.eventId] || {}}
                  currentUserId={effectiveUserId || ""}
                  allMessages={messages}
                />
              ))
            )}
            <div ref={messagesEndRef} />
          </List>

          {/* Typing Indicator */}
          <TypingIndicator users={typingUsers} />

          {/* Reply Bar */}
          {replyToMessage && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1,
                backgroundColor: "#e3f2fd",
                borderTop: "1px solid #bbdefb",
              }}
            >
              <ReplyIcon sx={{ mr: 1, color: "#1976d2", fontSize: 18 }} />
              <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#1976d2" }}
                >
                  Replying to {formatSender(replyToMessage.sender)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#666" }}
                  noWrap
                >
                  {replyToMessage.content}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setReplyToId(null)}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          {/* Edit Bar */}
          {editingId && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1,
                backgroundColor: "#fff3e0",
                borderTop: "1px solid #ffe0b2",
              }}
            >
              <EditIcon sx={{ mr: 1, color: "#f57c00", fontSize: 18 }} />
              <TextField
                fullWidth
                size="small"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleConfirmEdit();
                  }
                  if (e.key === "Escape") handleCancelEdit();
                }}
                autoFocus
                sx={{ mr: 1 }}
              />
              <IconButton
                size="small"
                color="primary"
                onClick={handleConfirmEdit}
              >
                <CheckIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CloseIcon />
              </IconButton>
            </Box>
          )}

          {/* Message Input */}
          {!editingId && (
            <>
            {/* Pending files preview */}
            {pendingFiles.length > 0 && (
              <Box sx={{ px: 2, pt: 1, borderTop: "1px solid #e0e0e0", backgroundColor: "#fafafa", display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                {pendingFiles.map((file, i) => (
                  <Chip
                    key={i}
                    label={file.name}
                    size="small"
                    onDelete={() => handleRemovePendingFile(i)}
                    icon={file.type.startsWith("image/") ? <ImageIcon sx={{ fontSize: 16 }} /> : <InsertDriveFileIcon sx={{ fontSize: 16 }} />}
                    sx={{ maxWidth: 200 }}
                  />
                ))}
                <Button
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: "none", minWidth: 0 }}
                >
                  + Add
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={() => setPendingFiles([])} sx={{ textTransform: "none" }}>Cancel</Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSendPendingFiles}
                  disabled={isSendingFiles}
                  sx={{ textTransform: "none" }}
                >
                  {isSendingFiles ? <CircularProgress size={16} /> : `Send ${pendingFiles.length > 1 ? `(${pendingFiles.length})` : ""}`}
                </Button>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "12px 16px",
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                gap: 0.5,
              }}
            >
              {/* Emoji button */}
              <IconButton
                size="small"
                onClick={(e) => setInputEmojiAnchor(e.currentTarget)}
                sx={{ color: "#999" }}
              >
                <EmojiEmotionsIcon />
              </IconButton>

              {/* Attachment button */}
              <IconButton
                size="small"
                onClick={(e) => setAttachMenuAnchor(e.currentTarget)}
                sx={{ color: "#999" }}
              >
                <AttachFileIcon />
              </IconButton>

              <TextField
                fullWidth
                variant="outlined"
                placeholder={replyToId ? "Type a reply..." : "Type a message..."}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                disabled={isLoading}
                size="small"
                multiline
                maxRows={4}
                sx={{ mr: 0.5 }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={isLoading || !inputValue.trim()}
              >
                <SendIcon />
              </IconButton>
            </Box>
            </>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this message? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forward Dialog */}
      <ForwardDialog
        open={!!forwardContent}
        content={forwardContent || ""}
        rooms={rooms.filter((r) => r.id !== currentRoom?.id)}
        onForward={handleForward}
        onClose={() => setForwardContent(null)}
      />

      {/* Attachment Menu */}
      <Menu
        anchorEl={attachMenuAnchor}
        open={Boolean(attachMenuAnchor)}
        onClose={() => setAttachMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setAttachMenuAnchor(null);
            imageInputRef.current?.click();
          }}
        >
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Photo / Video</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAttachMenuAnchor(null);
            fileInputRef.current?.click();
          }}
        >
          <ListItemIcon>
            <InsertDriveFileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>File</ListItemText>
        </MenuItem>
      </Menu>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileSelect}
        multiple
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*,video/*"
        style={{ display: "none" }}
        onChange={handleFileSelect}
        multiple
      />

      {/* Emoji picker for input */}
      <EmojiPickerPopover
        anchorEl={inputEmojiAnchor}
        open={Boolean(inputEmojiAnchor)}
        onClose={() => setInputEmojiAnchor(null)}
        onSelect={handleEmojiForInput}
      />
    </Paper>
  );
};

export default MatrixChatView;
